// chartPrepCore.js - pure helpers for Chart Prep: the day list, the detail
// modal, the morning huddle board and the provider dashboard layout.
//
// No React, no Supabase. Unit tests: src/lib/__tests__/chartPrep.test.mjs.
// Server calls live in chartPrepApi.js.

// ---------------------------------------------------------------------------
// Roles. Every check fails CLOSED: an unknown or missing role gets nothing.
// These mirror the server (chart_prep_can_see, chart_prep_mark_reviewed,
// chart_prep_edit_item, staff-chart-prep-action); the server is the real gate.
// ---------------------------------------------------------------------------
export const CHART_PREP_READ_ROLES = ["Owner", "Manager", "Provider", "Medical Assistant", "Front Desk", "RN", "LPN"];
export const CHART_PREP_ACTION_ROLES = ["Owner", "Manager", "Provider", "Medical Assistant", "RN", "LPN"];
export const CHART_PREP_REVIEW_ROLES = ["Owner", "Provider"];
export const CHART_PREP_EDIT_ROLES = ["Owner", "Provider"];

export function roleIn(role, list) {
  return typeof role === "string" && role.length > 0 && Array.isArray(list) && list.includes(role);
}
export const canReadChartPrep = (role) => roleIn(role, CHART_PREP_READ_ROLES);
export const canActOnChartPrep = (role) => roleIn(role, CHART_PREP_ACTION_ROLES);
export const canReviewChartPrep = (role) => roleIn(role, CHART_PREP_REVIEW_ROLES);
export const canEditChartPrep = (role) => roleIn(role, CHART_PREP_EDIT_ROLES);
// Send to Scribe is a Command-tier feature. Unknown tier: no.
export const scribeEnabledForTier = (tier) => tier === "Command";

// ---------------------------------------------------------------------------
// Server error codes -> sentences
// ---------------------------------------------------------------------------
const ERROR_TEXT = {
  not_allowed: "You are not signed in to a practice with access to chart prep.",
  role_not_permitted: "Your role cannot do that on chart prep.",
  not_found: "That chart prep note is not available to you.",
  note_failed: "This note did not generate, so there is nothing to review yet. Try Regenerate.",
  note_changed: "This note was refreshed while you had it open. Close it and open it again to see the new version.",
  note_too_long: "The pre-visit note is too long (2,000 characters at most).",
  text_too_long: "That text is too long (1,000 characters at most).",
  bad_section: "That part of the note cannot be edited.",
  item_not_found: "That item is no longer in the note. Reopen the note.",
  bad_rating: "Feedback must be thumbs up or thumbs down.",
  bad_status: "That task status is not recognised.",
  bad_source: "That task source is not recognised.",
  bad_task_key: "That prep task could not be saved.",
  bad_label: "A prep task needs a short label.",
  provider_not_found: "That provider is not in your practice.",
  assignee_not_allowed: "Only an active medical assistant, RN or LPN in your practice can be assigned.",
  assignee_not_in_practice: "That person is not an active member of your practice.",
  too_many: "Too many notes at once. Narrow the date.",
  bad_date: "Pick a date first.",
};

export function chartPrepErrorText(err) {
  const msg = String((err && (err.message || err.error)) || err || "");
  for (const code of Object.keys(ERROR_TEXT)) {
    if (msg.includes(code)) return ERROR_TEXT[code];
  }
  return msg || "Something went wrong.";
}

// ---------------------------------------------------------------------------
// Dates and times (deterministic; no toLocaleString so tests are stable)
// ---------------------------------------------------------------------------
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function localDateStr(d) {
  const dt = d || new Date();
  return dt.getFullYear() + "-" + String(dt.getMonth() + 1).padStart(2, "0") + "-" + String(dt.getDate()).padStart(2, "0");
}

export function shortDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return "";
  return MONTHS[Number(m[2]) - 1] + " " + Number(m[3]);
}

export function clockTime(d) {
  const h24 = d.getHours();
  const h12 = ((h24 + 11) % 12) + 1;
  return h12 + ":" + String(d.getMinutes()).padStart(2, "0") + " " + (h24 < 12 ? "AM" : "PM");
}

// "Data as of Oct 3, 6:10 PM" (local time). Null when unknown.
export function dataAsOfText(note) {
  const raw = note && (note.data_as_of || note.last_attempt_at);
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return "Data as of " + MONTHS[d.getMonth()] + " " + d.getDate() + ", " + clockTime(d);
}

// start_slot is 15-minute slots from midnight.
export function formatSlotTime(slot) {
  if (slot === null || slot === undefined || slot === "") return "";
  const totalMinutes = Number(slot) * 15;
  if (!Number.isFinite(totalMinutes)) return "";
  const h24 = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const h12 = ((h24 + 11) % 12) + 1;
  return h12 + ":" + String(m).padStart(2, "0") + " " + (h24 < 12 ? "AM" : "PM");
}

// The default date from chart_prep_default_date(), else the local date.
export function pickDefaultDate(rpcResult, now) {
  const d = rpcResult && typeof rpcResult.date === "string" ? rpcResult.date : null;
  if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return localDateStr(now || new Date());
}

// ---------------------------------------------------------------------------
// Note status, honesty banners, staleness
// ---------------------------------------------------------------------------
export function noteStatusInfo(note) {
  if (!note) return { label: "No prep", tone: "neutral" };
  if (note.status === "Failed") return { label: "Failed", tone: "red" };
  if (note.status === "Reviewed") return { label: "Reviewed", tone: "green" };
  if (note.context_status === "partial") return { label: "Partial", tone: "amber" };
  return { label: "Ready", tone: "blue" };
}

export function missingSectionsText(note) {
  const list = note && Array.isArray(note.missing_sections) ? note.missing_sections.filter(Boolean) : [];
  if (list.length === 0) return null;
  return "Could not read " + list.join(", ") + " when this note was made, so it may be missing those details.";
}

const STALE_WORDS = {
  lab: ["new lab result", "new lab results"],
  adt: ["new hospital or ED event", "new hospital or ED events"],
  encounter: ["new visit note", "new visit notes"],
  measurement: ["new measurement", "new measurements"],
};

// reasons: [{ type, count, latest_at }] from chart_prep_staleness
export function staleText(stale) {
  const reasons = stale && Array.isArray(stale.reasons) ? stale.reasons : [];
  if (!stale || !stale.stale || reasons.length === 0) return null;
  const parts = reasons.map((r) => {
    const w = STALE_WORDS[r.type] || ["new record", "new records"];
    const n = Number(r.count) || 0;
    return n + " " + (n === 1 ? w[0] : w[1]);
  });
  return parts.join(", ") + " since this note was made.";
}

export function indexStaleness(list) {
  const map = {};
  for (const s of list || []) if (s && s.note_id) map[s.note_id] = s;
  return map;
}

export function flagCounts(flags) {
  const counts = { high: 0, medium: 0, low: 0 };
  if (!Array.isArray(flags)) return counts;
  for (const f of flags) {
    const sev = (f && f.severity ? String(f.severity) : "").toLowerCase();
    if (sev === "high") counts.high += 1;
    else if (sev === "medium") counts.medium += 1;
    else if (sev === "low") counts.low += 1;
  }
  return counts;
}

const SEV_RANK = { high: 0, medium: 1, low: 2 };
export function topFlags(note, n) {
  const flags = note && Array.isArray(note.flags) ? note.flags.slice() : [];
  flags.sort((a, b) => (SEV_RANK[String((a && a.severity) || "low").toLowerCase()] ?? 3)
    - (SEV_RANK[String((b && b.severity) || "low").toLowerCase()] ?? 3));
  return flags.slice(0, n || 3);
}

// ---------------------------------------------------------------------------
// Items and citations
// ---------------------------------------------------------------------------
export const ITEM_SECTIONS = ["key_problems", "overdue_measures", "recent_changes", "suggested_agenda", "flags",
  "device_notes", "vaccine_notes", "care_gaps"];

export function itemText(section, item) {
  if (item === null || item === undefined) return "";
  if (typeof item === "string") return item;
  switch (section) {
    case "key_problems": return String(item.name || "");
    case "overdue_measures": return String(item.measure || "");
    case "recent_changes": return String(item.summary || "");
    case "device_notes": return String(item.device || "");
    case "vaccine_notes": return String(item.vaccine || "");
    case "care_gaps": return String(item.measure_name || item.measure_code || "");
    default: return String(item.text || item.summary || item.name || "");
  }
}

// A note written by prompt v4+ carries citation_stats; older notes do not,
// and their items are shown without a verified/unverified marker.
export function noteIsCited(note) {
  return !!(note && note.citation_stats && typeof note.citation_stats.items === "number");
}

// Free-text sections (summary, arc...) are not itemised or cited.
const TEXT_SECTIONS = ["one_line_summary", "chief_reason", "historical_arc", "sdoh_summary"];

export function normalizeItem(section, item, cited) {
  const base = typeof item === "string" ? { text: item } : { ...(item || {}) };
  const sources = Array.isArray(base.sources) ? base.sources.filter((s) => s && s.type && s.id !== undefined) : [];
  let verified = null;
  if (TEXT_SECTIONS.includes(section)) verified = null;
  // Only pipeline lock-in flags are trusted without a citation.
  else if (base.deterministic && section === "flags") verified = true;
  else if (cited) verified = base.verified === true || (base.verified === undefined && sources.length > 0);
  return { ...base, text: itemText(section, item), sources, verified };
}

// cm_risk_tier "Tier 1".."Tier 6"; Tier 5-6 = high (PRL H), 3-4 = medium, 1-2 = low.
export function riskLevel(tier) {
  const m = /Tier\s*([1-6])/i.exec(String(tier || ""));
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 5 ? "high" : n >= 3 ? "medium" : "low";
}

export function citationSummary(note) {
  const s = note && note.citation_stats;
  if (!s || typeof s.items !== "number") return null;
  const unverified = Number(s.unverified) || 0;
  if (s.items === 0) return null;
  if (unverified === 0) return "Every item cites the record it came from.";
  return unverified + " of " + s.items + " items have no verified source. Check them against the chart.";
}

const SOURCE_META = {
  lab: { label: "Lab", tab: "labs" },
  measurement: { label: "Vital", tab: "trends" },
  encounter: { label: "Visit", tab: "notes" },
  medication: { label: "Med", tab: "meds" },
  message: { label: "Message", tab: "messages" },
  screener: { label: "Screening", tab: "screening" },
  hrsn: { label: "HRSN", tab: "screening" },
  appointment: { label: "Appt", tab: "appts" },
  device: { label: "Device", tab: "info" },
  gap: { label: "Care gap", tab: "hedis" },
  adt: { label: "Hospital event", tab: "transitions" },
  transition: { label: "Transition", tab: "transitions" },
  goal: { label: "Goal", tab: "plan" },
  risk: { label: "Risk", tab: "risk" },
  problem: { label: "Problem list", tab: "info" },
  allergy: { label: "Allergy", tab: "info" },
  vaccine_pref: { label: "Vaccine pref", tab: "immunizations" },
  lockin: { label: "Lock-in", tab: "meds" },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function sourceChipText(src) {
  if (!src) return "";
  const meta = SOURCE_META[src.type] || { label: "Record" };
  return meta.label + (src.date ? " " + shortDate(src.date) : "");
}

// Where a source chip goes. A visit opens the clinical note itself; every
// other source opens the patient chart tab that holds it.
export function sourceLink(src, patientId) {
  if (!src || !src.type) return null;
  if (src.type === "encounter" && UUID_RE.test(String(src.id || ""))) {
    return { to: "/clinical?encounter=" + src.id, title: "Open this visit note" };
  }
  const meta = SOURCE_META[src.type];
  if (!meta || !patientId) return null;
  return { to: "/patients/" + patientId + "/" + meta.tab, title: "Open " + meta.label.toLowerCase() + " in the chart" };
}

// ---------------------------------------------------------------------------
// Provider edits and feedback
// ---------------------------------------------------------------------------
export function itemKey(section, index) {
  return section + ":" + (index === null || index === undefined ? "" : index);
}

export function indexEdits(edits) {
  const map = {};
  for (const e of edits || []) if (e && e.section) map[itemKey(e.section, e.item_index)] = e;
  return map;
}

// Note-level feedback has section null.
export function indexFeedback(list) {
  const map = {};
  for (const f of list || []) {
    if (!f) continue;
    map[f.section ? itemKey(f.section, f.item_index) : "note"] = f;
  }
  return map;
}

// ---------------------------------------------------------------------------
// Huddle: MA prep tasks derived from open gaps, overdue measures and agenda
// ---------------------------------------------------------------------------
// Standing-order style prep, keyed by category so a gap and an overdue
// measure that need the same prep become one checkbox.
const PREP_CATEGORIES = [
  { key: "bp", label: "Repeat BP if the first reading is 140/90 or higher", codes: ["CBP", "BPD", "CBP-E"], words: ["blood pressure", " bp ", "bp control", "hypertension"] },
  { key: "a1c", label: "Point-of-care A1C if none in the last 3 months", codes: ["HBD", "GSD", "CDC", "HBA1C", "A1C"], words: ["a1c", "hba1c", "glycemic"] },
  { key: "eye", label: "Diabetic eye exam: retinal camera or referral", codes: ["EED"], words: ["retinal", "eye exam"] },
  { key: "kidney", label: "Kidney health: urine albumin-creatinine and eGFR order", codes: ["KED"], words: ["uacr", "microalbumin", "albumin-creatinine", "kidney health"] },
  { key: "lipids", label: "Lipid panel order if due", codes: [], words: ["lipid", "cholesterol", "ldl"] },
  { key: "foot", label: "Monofilament foot exam setup", codes: [], words: ["foot exam", "monofilament"] },
  { key: "mammo", label: "Mammogram order or scheduling", codes: ["BCS", "BCS-E"], words: ["mammogram", "breast cancer screening"] },
  { key: "cervical", label: "Cervical cancer screening: prep the exam room", codes: ["CCS", "CCS-E"], words: ["pap", "cervical"] },
  { key: "colorectal", label: "Colorectal screening: FIT kit or referral", codes: ["COL", "COL-E"], words: ["colorectal", "colon", "fit kit", "colonoscopy"] },
  { key: "chlamydia", label: "Chlamydia screening: urine specimen", codes: ["CHL"], words: ["chlamydia"] },
  { key: "immunizations", label: "Review immunization record; prepare due vaccines per standing order", codes: ["CIS", "CIS-E", "IMA", "IMA-E", "AIS", "AIS-E", "FLU"], words: ["vaccine", "immuniz", "flu shot", "influenza"] },
  { key: "wellchild", label: "Well-child: growth and development screening forms", codes: ["W30", "WCV", "WCC"], words: ["well-child", "well child"] },
  { key: "lead", label: "Lead screening: capillary lead test", codes: ["LSC"], words: ["lead screening", "lead level"] },
  { key: "depression", label: "Depression screening: PHQ-9 at intake", codes: ["DSF", "DSF-E", "DMS", "DMS-E", "DEP"], words: ["phq", "depression screen"] },
  { key: "prenatal", label: "Prenatal or postpartum screening forms", codes: ["PPC", "PND", "PND-E", "PDS", "PDS-E"], words: ["postpartum", "prenatal"] },
  { key: "weight", label: "Height and weight for BMI", codes: ["WCC", "ABA"], words: ["bmi", "weight check"] },
];

function categoryForCode(code) {
  const c = String(code || "").toUpperCase();
  if (!c) return null;
  return PREP_CATEGORIES.find((p) => p.codes.some((x) => c === x || c.startsWith(x + "-") || c.startsWith(x + "_"))) || null;
}

function categoryForText(text) {
  const t = " " + String(text || "").toLowerCase() + " ";
  return PREP_CATEGORIES.find((p) => p.words.some((w) => t.includes(w))) || null;
}

function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "item";
}

// Returns [{ task_key, label, source_type, detail }] in a stable order:
// gap-derived first (they are what the plan pays on), then overdue measures,
// then agenda items that match a prep category.
export function deriveHuddleTasks(note) {
  if (!note) return [];
  const out = [];
  const seen = new Set();
  const add = (task) => {
    if (!task || seen.has(task.task_key)) return;
    seen.add(task.task_key);
    out.push(task);
  };
  for (const g of Array.isArray(note.care_gaps) ? note.care_gaps : []) {
    const cat = categoryForCode(g.measure_code) || categoryForText(g.measure_name);
    if (cat) add({ task_key: "prep:" + cat.key, label: cat.label, source_type: "gap", detail: g.measure_code || g.measure_name || null });
    else add({ task_key: "gap:" + slug(g.measure_code || g.measure_name), label: "Close care gap: " + (g.measure_name || g.measure_code), source_type: "gap", detail: g.measure_code || null });
  }
  for (const m of Array.isArray(note.overdue_measures) ? note.overdue_measures : []) {
    const text = itemText("overdue_measures", m);
    const cat = categoryForText(text) || categoryForCode(text);
    if (cat) add({ task_key: "prep:" + cat.key, label: cat.label, source_type: "overdue", detail: text });
  }
  for (const a of Array.isArray(note.suggested_agenda) ? note.suggested_agenda : []) {
    const cat = categoryForText(itemText("suggested_agenda", a));
    if (cat) add({ task_key: "prep:" + cat.key, label: cat.label, source_type: "agenda", detail: null });
  }
  return out;
}

// Derived tasks carry the persisted status; persisted tasks that are no longer
// derived (note regenerated) still show, so a checked box never vanishes.
export function mergeHuddleTasks(derived, persisted) {
  const byKey = {};
  for (const p of persisted || []) if (p && p.task_key) byKey[p.task_key] = p;
  const out = (derived || []).map((d) => {
    const p = byKey[d.task_key];
    return { ...d, status: p ? p.status : "open", completed_by_name: p ? p.completed_by_name || null : null };
  });
  const have = new Set(out.map((t) => t.task_key));
  for (const p of persisted || []) {
    if (p && p.task_key && !have.has(p.task_key)) out.push({ ...p, detail: null });
  }
  return out;
}

export function taskProgress(tasks) {
  const list = tasks || [];
  const done = list.filter((t) => t.status === "done" || t.status === "not_needed").length;
  return { done, total: list.length };
}

export function shortPatientName(first, last) {
  const f = String(first || "").trim();
  const l = String(last || "").trim();
  if (!f && !l) return "Patient";
  return (f || "Patient") + (l ? " " + l.charAt(0).toUpperCase() + "." : "");
}

// data: chart_prep_huddle() result. Returns provider groups in name order,
// each with its rows (tasks merged) and progress.
export function groupHuddle(data) {
  const rows = data && Array.isArray(data.rows) ? data.rows : [];
  const assign = {};
  for (const a of (data && data.assignments) || []) if (a && a.provider_id) assign[a.provider_id] = a;
  const groups = new Map();
  for (const r of rows) {
    const key = r.provider_id || "none";
    if (!groups.has(key)) {
      groups.set(key, {
        provider_id: r.provider_id || null,
        provider_name: r.provider_name || (r.provider_id ? "Provider" : "No provider"),
        provider_last_name: r.provider_last_name || "",
        assignee_id: assign[r.provider_id] ? assign[r.provider_id].assignee_id : null,
        assignee_name: assign[r.provider_id] ? assign[r.provider_id].assignee_name : null,
        rows: [],
      });
    }
    const tasks = r.note ? mergeHuddleTasks(deriveHuddleTasks(r.note), r.tasks) : [];
    groups.get(key).rows.push({ ...r, tasksMerged: tasks });
  }
  const out = Array.from(groups.values()).map((g) => {
    const allTasks = g.rows.flatMap((r) => r.tasksMerged);
    const p = taskProgress(allTasks);
    const withNotes = g.rows.filter((r) => r.note && r.note.status !== "Failed");
    return {
      ...g,
      progress: {
        patients: g.rows.length,
        notes: withNotes.length,
        reviewed: withNotes.filter((r) => r.note.status === "Reviewed").length,
        tasks_done: p.done,
        tasks_total: p.total,
      },
    };
  });
  out.sort((a, b) => {
    if (!a.provider_id) return 1;
    if (!b.provider_id) return -1;
    return String(a.provider_last_name || a.provider_name).localeCompare(String(b.provider_last_name || b.provider_name));
  });
  return out;
}

// Minimum necessary for paper: time, first name + last initial, visit type,
// open prep tasks, gap measure codes and flag counts. No DOB, MRN, summary,
// flag text, diagnoses or HRSN.
export function huddlePrintRows(group) {
  return (group && group.rows ? group.rows : []).map((r) => {
    const counts = flagCounts(r.note && r.note.flags);
    const flagBits = [];
    if (counts.high) flagBits.push(counts.high + " high");
    if (counts.medium) flagBits.push(counts.medium + " med");
    return {
      time: formatSlotTime(r.start_slot),
      patient: shortPatientName(r.first_name, r.last_name),
      type: r.appt_type || "",
      tasks: (r.tasksMerged || []).filter((t) => t.status === "open").map((t) => t.label),
      gaps: (r.note && Array.isArray(r.note.care_gaps) ? r.note.care_gaps : []).map((g) => g.measure_code).filter(Boolean),
      flags: flagBits.join(", "),
      prep: r.note ? (r.note.status === "Failed" ? "No prep" : "Prep ready") : "No prep",
    };
  });
}

// ---------------------------------------------------------------------------
// Provider dashboard layout (order + hidden), saved per user
// ---------------------------------------------------------------------------
export const PROVIDER_DASHBOARD_SECTIONS = [
  { id: "kpis", label: "Key numbers" },
  { id: "today", label: "Today's schedule, panel and queue" },
  { id: "worklist", label: "My worklist" },
  { id: "chart_prep", label: "Chart prep" },
];

export function defaultDashboardLayout() {
  return { order: PROVIDER_DASHBOARD_SECTIONS.map((s) => s.id), hidden: [] };
}

export function normalizeDashboardLayout(saved) {
  const ids = PROVIDER_DASHBOARD_SECTIONS.map((s) => s.id);
  const order = [];
  for (const id of (saved && Array.isArray(saved.order) ? saved.order : [])) {
    if (ids.includes(id) && !order.includes(id)) order.push(id);
  }
  for (const id of ids) if (!order.includes(id)) order.push(id);
  const hidden = (saved && Array.isArray(saved.hidden) ? saved.hidden : []).filter((id, i, a) => ids.includes(id) && a.indexOf(id) === i);
  return { order, hidden };
}

export function moveDashboardSection(layout, id, delta) {
  const l = normalizeDashboardLayout(layout);
  const i = l.order.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= l.order.length) return l;
  const order = l.order.slice();
  order.splice(i, 1);
  order.splice(j, 0, id);
  return { ...l, order };
}

// Drag and drop: put `id` where `targetId` is.
export function moveDashboardSectionTo(layout, id, targetId) {
  const l = normalizeDashboardLayout(layout);
  const i = l.order.indexOf(id);
  const j = l.order.indexOf(targetId);
  if (i < 0 || j < 0 || i === j) return l;
  const order = l.order.slice();
  order.splice(i, 1);
  order.splice(j, 0, id);
  return { ...l, order };
}

export function toggleDashboardSection(layout, id) {
  const l = normalizeDashboardLayout(layout);
  const hidden = l.hidden.includes(id) ? l.hidden.filter((x) => x !== id) : l.hidden.concat(id);
  return { ...l, hidden };
}

export function dashboardLayoutKey(userId) {
  return "pos.providerDashboard.layout.v1." + (userId || "anon");
}

export function isDefaultDashboardLayout(layout) {
  const l = normalizeDashboardLayout(layout);
  const d = defaultDashboardLayout();
  return l.hidden.length === 0 && l.order.join(",") === d.order.join(",");
}
